import {
  IconCircleCheck, IconCircleX,
  IconDownload, IconEye,
  IconFileTypePdf,
  IconLoader2,
  IconTrash,
} from "@tabler/icons-react";
import { ReactNode } from "react";


export interface UploadCardProps {
  file: File;
  className?: string;
  icon?: null | ReactNode;
  type?: UploadCardType | `${UploadCardType}`;
  supportiveLabel?: string;
  loading?: boolean;
  download?: boolean;
  view?: boolean;
  onRemove?: () => void;
  /** Should the error card use the trash icon instead of an X. */
  errorUsesDelete?: boolean;
}

export enum UploadCardType {
  base = "base",
  error = "error",
  success = "success",
}

const cardStyle: Record<UploadCardType, string> = {
  base: "bg-bg-components-base border-border-base",
  error: "bg-bg-components-base border-border-danger-base outline-input-danger outline-offset-0",
  success: "bg-bg-components-base border-border-success outline-input-success outline-offset-0",
};

const renderIcon = (fileName: string, icon: ReactNode | null) => <div className="w-5">{icon ?? <IconFileTypePdf />}</div>;

const downloadFile = (file: File) => {
  const fileURL = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = fileURL;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(fileURL);
};

const previewFile = (file: File) => {
  const fileURL = URL.createObjectURL(file);
  window.open(fileURL, "_blank");
};

const UploadCard = ({
  file, icon, supportiveLabel, onRemove, className, type = UploadCardType.base, loading = false, download = true, view = true, errorUsesDelete,
}: UploadCardProps) => {
  const renderIcons = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center w-10 h-10">
          <IconLoader2 className="text-icon-bg-base animate-spin" />
        </div>
      );
    }
    if (type === UploadCardType.error) {
      return (
        <div className="flex items-center justify-center w-10 h-10">
          {
            errorUsesDelete
              ? <IconTrash className="text-icon-danger cursor-pointer" onClick={() => onRemove()} />
              : <IconCircleX className="text-icon-danger cursor-pointer" onClick={() => onRemove()} />
          }
        </div>
      );
    }
    if (type === UploadCardType.success) {
      return (
        <div className="flex items-center justify-center w-10 h-10">
          <IconCircleCheck className="text-icon-success" />
        </div>
      );
    }
    return (
      <div className="flex">
        {download && (
          <div className="flex items-center justify-center w-10 h-10">
            <IconDownload className="text-icon-bg-subtle cursor-pointer" onClick={() => downloadFile(file)} />
          </div>
        )}
        {view && (
          <div className="flex items-center justify-center w-10 h-10">
            <IconEye className="text-icon-bg-subtle cursor-pointer" onClick={() => previewFile(file)} />
          </div>
        )}
        {onRemove && (
          <div className="flex items-center justify-center w-10 h-10">
            <IconTrash className="text-icon-bg-subtle cursor-pointer" onClick={() => onRemove()} />
          </div>
        )}
      </div>
    );
  };

  const getTextColor = () => {
    if (loading) {
      return "text-text-interactive-base";
    }
    if (type === UploadCardType.error) {
      return "text-text-danger";
    }
    return "text-text-subtle";
  };

  return (
    <div className={`sds w-[300px] ${className} min-h-14 border rounded-sm p-2 flex justify-between select-none ${cardStyle[type]}`}>
      <div className="flex items-center gap-2">
        {renderIcon(file.name, icon)}
        <div className="flex flex-col">
          <span className="text-text-base typography-medium-base">{file.name}</span>
          {supportiveLabel && (
            <span className={`typography-small-base ${getTextColor()}`}>
              {supportiveLabel}
            </span>
          )}
        </div>
      </div>
      {renderIcons()}
    </div>
  );
};

export default UploadCard;
